import React from "react";
import { Box, Button, Paper, Typography, useTheme } from "@mui/material";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import Layout from "../components/layout/Layout";
import Eyebrow from "../components/brand/Eyebrow";
import { fonts } from "../theme/tokens";
import { useAuth } from "../context/AuthContext";
import { useMsal } from "@azure/msal-react";
import { getLoginRedirectUri, relayState } from "../authConfig";

const initialsFor = (user) => {
  if (!user) return "?";
  if (user.name) {
    return user.name
      .split(/\s+/)
      .filter(Boolean)
      .map((part) => part[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  }
  if (user.username) return user.username[0].toUpperCase();
  return "?";
};

export default function ProfilePage() {
  const theme = useTheme();
  const { user, logoutLocal } = useAuth();
  const { instance } = useMsal();

  const role = user?.claims_role?.[0] || user?.roles?.[0] || "";
  const email = user?.email || user?.username || "";

  const handleLogout = async () => {
    try {
      await instance.logoutPopup({
        postLogoutRedirectUri: getLoginRedirectUri(),
        state: relayState(),
      });
    } catch (error) {
      console.warn("Logout popup failed:", error);
    } finally {
      logoutLocal("You have been logged out.");
    }
  };

  return (
    <Layout eyebrow="Your account" title="Profile">
      <Box sx={{ maxWidth: 920 }}>
        <Box
          sx={{
            backgroundColor: theme.palette.brand.black,
            color: "#fff",
            borderRadius: "8px",
            p: "22px 26px",
            display: "flex",
            gap: 2.75,
            alignItems: "center",
            mb: 3,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 3,
              backgroundColor: theme.palette.brand.cyan,
            }}
          />
          <Box
            sx={{
              width: 68,
              height: 68,
              borderRadius: "50%",
              backgroundColor: theme.palette.brand.gold,
              color: theme.palette.brand.black,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 24,
              fontWeight: 700,
              fontFamily: fonts.serif,
              flexShrink: 0,
            }}
          >
            {initialsFor(user)}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Eyebrow sx={{ color: theme.palette.brand.gold, mb: 0.5 }}>
              {role || "User"}
            </Eyebrow>
            <Typography
              sx={{
                fontFamily: fonts.serif,
                fontSize: 28,
                fontWeight: 600,
                lineHeight: 1.1,
                m: 0,
              }}
            >
              {user?.name || user?.username || "—"}
            </Typography>
            {email && (
              <Box
                sx={{
                  fontSize: 13,
                  color: "rgba(255,255,255,0.65)",
                  mt: 0.75,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                }}
              >
                <MailOutlineRoundedIcon sx={{ fontSize: 13 }} />
                {email}
              </Box>
            )}
          </Box>
          <Button
            variant="outlined"
            size="small"
            onClick={handleLogout}
            startIcon={<LogoutOutlinedIcon sx={{ fontSize: 14 }} />}
            sx={{
              color: "rgba(255,255,255,0.85)",
              borderColor: "rgba(255,255,255,0.25)",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.06)",
                borderColor: "rgba(255,255,255,0.45)",
              },
            }}
          >
            Sign out
          </Button>
        </Box>

        <Paper
          sx={{
            border: `1px solid ${theme.palette.brand.border}`,
            borderRadius: "8px",
            p: { xs: 2.5, md: "24px 28px" },
          }}
        >
          <Typography
            sx={{
              fontFamily: fonts.serif,
              fontSize: 22,
              fontWeight: 600,
              mb: 1,
            }}
          >
            Account details
          </Typography>
          <Typography sx={{ fontSize: 13, color: theme.palette.brand.inkSoft, mb: 3 }}>
            Profile editing is not available here yet.
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "180px 1fr" },
              gap: "12px 20px",
            }}
          >
            <Typography fontWeight={600}>Full name</Typography>
            <Typography>{user?.name || "—"}</Typography>

            <Typography fontWeight={600}>Email</Typography>
            <Typography>{email || "—"}</Typography>

            <Typography fontWeight={600}>Role</Typography>
            <Typography>{role || "—"}</Typography>
          </Box>
        </Paper>
      </Box>
    </Layout>
  );
}
